import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, CheckIcon, Loader2Icon, SendIcon } from 'lucide-react';
import { KaykoLogo } from '../components/KaykoLogo';
import { SmartLink } from '../components/SmartLink';
import { PhoneField } from '../components/form/PhoneField';
import { BusinessTypeCombobox } from '../components/form/BusinessTypeCombobox';
import { inputBase, inputBorder } from '../components/form/fieldStyles';
import { useRequestDemoForm, type DemoField } from '../hooks/useRequestDemoForm';
export function RequestDemo() {
  const [params] = useSearchParams();
  const {
    values,
    errors,
    status,
    sendError,
    setValue,
    blur,
    submit
  } = useRequestDemoForm(params.get('email') ?? '');
  const submitting = status === 'submitting';
  return <main className="hero-gradient flex min-h-screen w-full flex-col items-center px-4 pb-16 pt-8 sm:px-6" data-id="element-2142" data-mp="2nxbgz41k2sg9">
      <div className="flex w-full max-w-[520px] items-center justify-between" data-id="element-2143" data-mp="2jtdk6zzpdtqd">
        <SmartLink href="/" className="inline-flex items-center gap-2 text-[14px] font-medium text-white/85 transition-colors duration-150 ease-out hover:text-white" data-id="element-2144" data-mp="29ikhuum6fcpy">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-2145" data-mp="20ft8d0cv2r0y" />
          Back to website
        </SmartLink>
        <span className="text-[14px] text-white/70" data-id="element-2146" data-mp="24h529i2rpyey">Takes about a minute</span>
      </div>

      <div className="mt-5 w-full max-w-[520px] rounded-[16px] bg-white px-6 py-9 shadow-[0_24px_60px_-20px_rgba(8,12,50,0.55)] sm:px-10" data-id="element-2147" data-mp="1qq9mdltuqh1v">
        <div className="flex justify-center" data-id="element-2148" data-mp="1vjkwpwxvhfyj">
          <KaykoLogo data-id="element-2149" data-mp="1gtn5f84nca54" />
        </div>

        {status === 'success' ? <div className="py-6 text-center" role="status" data-id="element-2150" data-mp="1lrox2hg51hs7">
            <SentIllustration data-id="element-2151" data-mp="1ls82iyolqdpc" />
            <h1 className="mt-6 text-[26px] font-semibold text-ink" data-id="element-2152" data-mp="17s71nj8c769v">Request sent</h1>
            <p className="mx-auto mt-3 max-w-[360px] text-[15px] leading-[1.55] text-ink-muted" data-id="element-2153" data-mp="17ujj7va6neme">
              Thanks, {values.name.split(' ')[0]}. A product specialist will contact you at {values.email} within 1
              business day to schedule your demo.
            </p>
            <SmartLink href="/" className="mt-8 inline-flex h-12 items-center justify-center rounded-[10px] bg-brand px-6 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover" data-id="element-2154" data-mp="39oek5sqt3vko">
              Back to homepage
            </SmartLink>
          </div> : <>
            <h1 className="mt-7 text-center text-[26px] font-semibold text-ink" data-id="element-2155" data-mp="3sfz1tzpeijjb">Request a demo</h1>
            <p className="mt-2 text-center text-[15px] text-ink-muted" data-id="element-2156" data-mp="3odu8z36s1qxq">
              See how Kayko fits your business. We’ll reach out within 1 business day.
            </p>

            <form noValidate onSubmit={submit} className="mt-8 space-y-5" data-id="element-2157" data-mp="2cz6zemy0hqht">
              <Field id="name" label="Full name" error={errors.name} data-id="element-2158" data-mp="2cx3y36resaxs">
                <input id="demo-name" type="text" autoComplete="name" placeholder="Aline Uwimana" value={values.name} onChange={e => setValue('name', e.target.value)} onBlur={() => blur('name')} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'demo-name-error' : undefined} className={`${inputBase} ${inputBorder(Boolean(errors.name))}`} data-id="element-2159" data-mp="27ysp0y8xu4gm" />
              </Field>

              <Field id="email" label="Email" error={errors.email} data-id="element-2160" data-mp="1b6un574kvni6">
                <input id="demo-email" type="email" inputMode="email" autoComplete="email" placeholder="you@yourbusiness.com" value={values.email} onChange={e => setValue('email', e.target.value)} onBlur={() => blur('email')} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'demo-email-error' : undefined} className={`${inputBase} ${inputBorder(Boolean(errors.email))}`} data-id="element-2161" data-mp="1g79yjt5aukxi" />
              </Field>

              <Field id="phone" label="Phone number" error={errors.phone} data-id="element-2162" data-mp="30mwz2o1tvnis">
                <PhoneField id="demo-phone" value={values.phone} country={values.country} hasError={Boolean(errors.phone)} required describedBy={errors.phone ? 'demo-phone-error' : undefined} onChange={v => setValue('phone', v)} onCountryChange={c => setValue('country', c)} onBlur={() => blur('phone')} data-id="element-2163" data-mp="2vodcqmjbo9pw" />
              </Field>

              <Field id="businessName" label="Business name" error={errors.businessName} data-id="element-2164" data-mp="1p818c3pwpvlv">
                <input id="demo-businessName" type="text" autoComplete="organization" placeholder="e.g. Inzozi Mini Market" value={values.businessName} onChange={e => setValue('businessName', e.target.value)} onBlur={() => blur('businessName')} aria-invalid={Boolean(errors.businessName)} aria-describedby={errors.businessName ? 'demo-businessName-error' : undefined} className={`${inputBase} ${inputBorder(Boolean(errors.businessName))}`} data-id="element-2165" data-mp="1ka861lrq5lqv" />
              </Field>

              <Field id="businessType" label="Business type" error={errors.businessType} data-id="element-2166" data-mp="nevzugv54lic">
                <BusinessTypeCombobox id="demo-businessType" value={values.businessType} hasError={Boolean(errors.businessType)} describedBy={errors.businessType ? 'demo-businessType-error' : undefined} onChange={v => setValue('businessType', v)} onBlur={() => blur('businessType')} data-id="element-2167" data-mp="9jl8hdg3uiiq" />
              </Field>

              <Field id="message" label="Message" optional data-id="element-2168" data-mp="zrrevibe7ntp">
                <textarea id="demo-message" rows={4} placeholder="Tell us what you’d like to see, or how you run your business today." value={values.message} onChange={e => setValue('message', e.target.value)} className="w-full resize-none rounded-[10px] border border-ink/15 bg-white px-4 py-3 text-[16px] text-ink outline-none transition-[border-color,box-shadow] duration-150 ease-out placeholder:text-ink-muted/70 hover:border-ink/30 focus:border-brand focus:ring-2 focus:ring-brand/15" data-id="element-2169" data-mp="zs9emox9kk6d" />
              </Field>

              {sendError && <p role="alert" className="rounded-[10px] border border-[#C0362C]/25 bg-[#FDF1F0] px-4 py-3 text-[14px] leading-[1.5] text-[#8F2A22]" data-id="element-2170" data-mp="26emvxihfz8gu">
                  {sendError}
                </p>}

              <button type="submit" disabled={submitting} className="flex h-12 w-full items-center justify-center gap-2 rounded-[10px] bg-brand text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover disabled:cursor-wait disabled:opacity-80" data-id="element-2171" data-mp="3kbpivu5ezihf">
                {submitting && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-2172" data-mp="31dvexuj57x5w" />}
                {submitting ? 'Sending request…' : 'Request demo'}
              </button>

              <p className="text-center text-[13px] leading-[1.5] text-ink-muted" data-id="element-2173" data-mp="3ae1nd1zzi6y7">
                By submitting, you agree to our{' '}
                <SmartLink href="/legal/privacy" className="text-brand hover:text-brand-hover" data-id="element-2174" data-mp="3fdxv9cbsyij1">
                  Privacy Policy
                </SmartLink>
                .
              </p>
            </form>
          </>}
      </div>

    </main>;
}
function SentIllustration() {
  const reduce = useReducedMotion();
  return <div className="relative mx-auto h-28 w-28" aria-hidden="true" data-id="element-2175" data-mp="67zg01q661nx">
      <motion.span initial={reduce ? false : {
      scale: 0.96,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className="absolute inset-0 rounded-full bg-brand-soft" data-id="element-2176" data-mp="1ca89b3zxegj" />
      <span className="absolute inset-4 rounded-full bg-white shadow-[0_8px_24px_-10px_rgba(28,140,222,0.5)]" data-id="element-2177" data-mp="p01ylimdfs0t" />
      <motion.span initial={reduce ? false : {
      x: -14,
      y: 14,
      opacity: 0
    }} animate={{
      x: 0,
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.3,
      delay: 0.08,
      ease: [0.23, 1, 0.32, 1]
    }} className="absolute inset-0 flex items-center justify-center" data-id="element-2178" data-mp="k29vrtk6ulhu">
        <SendIcon className="h-10 w-10 -translate-x-0.5 translate-y-0.5 text-brand" strokeWidth={1.75} data-id="element-2179" data-mp="2zs34a0oq23rt" />
      </motion.span>
      <motion.span initial={reduce ? false : {
      scale: 0.96,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.2,
      delay: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} className="absolute bottom-1 right-1 flex h-8 w-8 items-center justify-center rounded-full border-[3px] border-white bg-brand" data-id="element-2180" data-mp="2vs2pug1mhskr">
        <CheckIcon className="h-4 w-4 text-white" strokeWidth={3} data-id="element-2181" data-mp="2bvgkqkpa085g" />
      </motion.span>
    </div>;
}
type FieldProps = {
  id: DemoField;
  label: string;
  error?: string;
  optional?: boolean;
  children: React.ReactNode;
};
function Field({
  id,
  label,
  error,
  optional,
  children
}: FieldProps) {
  return <div data-id="element-2182" data-mp="1syf2hy6pobjk">
      <label htmlFor={`demo-${id}`} className="mb-2 block text-[14px] font-medium text-ink" data-id="element-2183" data-mp="1syot9nzkymqb">
        {label}
        {optional && <span className="font-normal text-ink-muted" data-id="element-2184" data-mp="1xwqeofkhay4y"> (optional)</span>}
      </label>
      {children}
      {error && <p id={`demo-${id}-error`} className="mt-1.5 text-[13px] text-[#C0362C]" data-id="element-2185" data-mp="3ndrcj3y1d8ks">
          {error}
        </p>}
    </div>;
}